import { useEffect } from 'react'
import {
  createBrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom'
import * as Sentry from '@sentry/react'
import posthog from 'posthog-js'

import {
  DEFAULT_REPLAYS_ON_ERROR_SAMPLE_RATE,
  DEFAULT_REPLAYS_SESSION_SAMPLE_RATE,
  DEFAULT_TRACES_SAMPLE_RATE,
  SENTRY_FEATURE_FLAG,
} from './constants'

interface SentrySampleRates {
  tracesSampleRate?: number
  replaysSessionSampleRate?: number
  replaysOnErrorSampleRate?: number
}

export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN
  if (!dsn) return

  Sentry.init({
    dsn,
    enabled: import.meta.env.PROD || import.meta.env.MODE === 'staging',
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_SENTRY_RELEASE,
    integrations: [
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration(),
      Sentry.captureConsoleIntegration({ levels: ['error'] }),
      Sentry.consoleLoggingIntegration({ levels: ['warn', 'error', 'log', 'info'] }),
    ],
    tracesSampleRate: DEFAULT_TRACES_SAMPLE_RATE,
    tracePropagationTargets: [/localhost/, /api\.wisprflow\.ai/, /api-east\.wisprflow\.ai/],
    replaysSessionSampleRate: DEFAULT_REPLAYS_SESSION_SAMPLE_RATE,
    replaysOnErrorSampleRate: DEFAULT_REPLAYS_ON_ERROR_SAMPLE_RATE,
  })
}

/** Must be called after posthog.init() so the onFeatureFlags callback is registered. */
export function applySentrySampleRatesFromFeatureFlag(): void {
  posthog.onFeatureFlags(() => {
    const payload = posthog.getFeatureFlagPayload(SENTRY_FEATURE_FLAG) as SentrySampleRates | undefined
    if (!payload || typeof payload !== 'object') return

    const client = Sentry.getClient()
    if (!client) return

    const options = client.getOptions() as Sentry.BrowserOptions
    if (typeof payload.tracesSampleRate === 'number') {
      options.tracesSampleRate = payload.tracesSampleRate
    }
    if (typeof payload.replaysSessionSampleRate === 'number') {
      options.replaysSessionSampleRate = payload.replaysSessionSampleRate
    }
    if (typeof payload.replaysOnErrorSampleRate === 'number') {
      options.replaysOnErrorSampleRate = payload.replaysOnErrorSampleRate
    }
  })
}

export const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV7(createBrowserRouter)

interface SentryUserContext {
  id: string
  email: string
}

export function setSentryUser(user: SentryUserContext): void {
  Sentry.setUser({ id: user.id, email: user.email, ip_address: '{{auto}}' })
}

/** A login attempt has no session yet, so the attempted email is the only identity a failure can carry. */
export function setSentryLoginEmail(email: string): void {
  Sentry.setUser({ email, ip_address: '{{auto}}' })
}

export function clearSentryUser(): void {
  Sentry.setUser(null)
}
